import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type SupportedLocale = "en" | "es";

export const LOCALE_STORAGE_KEY = "gathered-locale";

const en = {
  "language.choose": "Choose language",
  "language.english": "English",
  "language.spanish": "Español",
  "brand.home": "Gathered home",
  "brand.descriptor": "memories held together",
  "progress.label": "Step {{current}} of {{total}}: {{label}}",
  "share.loadError": "The family collection is temporarily unavailable. Your draft will remain safely on this device.",
  "share.steps.about": "About you",
  "share.steps.photos": "Photos",
  "share.steps.story": "The story",
  "share.steps.review": "Review",
  "share.validation.name": "Please tell us your name so the family knows who shared this memory.",
  "share.validation.contact": "Please add either an email address or phone number so the family has a way to contact you about this memory.",
  "share.validation.email": "Please add a valid email address so the family can contact you about this memory.",
  "share.validation.phone": "Please check the phone number, or leave it blank.",
  "share.validation.photoStory": "Please add the story behind each photo before continuing. A sentence or two is enough.",
  "share.validation.storyDetail": "Please add a little more detail—one or two sentences is plenty.",
  "share.validation.storyRequired": "Please add the memory you would like the family to keep.",
  "share.validation.security": "Please complete the brief security check before sending your memory.",
  "share.validation.notImage": "{{filename}} is not an image. Please choose a JPG, PNG, HEIC, or WebP photo.",
  "share.validation.tooLarge": "{{filename}} is larger than 15 MB. Please choose a smaller copy.",
  "share.validation.tooMany": "You can add up to {{count}} photos in one memory. You can submit another memory afterward.",
  "share.submitError": "We couldn’t send your memory. Your draft is still saved on this device.",
  "share.complete.eyebrow": "Memory received",
  "share.complete.thanks": "Thank you, {{name}}.",
  "share.complete.lead": "Your story is now part of the collection the family is gathering for {{honoree}}. They’ll review it with care before it appears in any tribute.",
  "share.complete.meaningTitle": "You gave this memory its meaning.",
  "share.complete.meaningBody": "The names, place, and story you added are what turn a photograph into something the family can keep.",
  "share.complete.verifiedTitle": "Verified and recoverable",
  "share.complete.verifiedBody": "Every original was integrity-checked and stored in two protected locations before this receipt appeared.",
  "share.complete.receipt": "Receipt",
  "share.complete.record": "Record",
  "share.complete.moreTitle": "Have another moment to share?",
  "share.complete.moreBody": "There’s no limit to the memories you can add. Your name and contact details will already be filled in.",
  "share.complete.another": "Add another memory",
  "share.complete.visit": "View the memorial",
  "share.complete.receivedNoPhotos": "1 story safely received",
  "share.complete.receivedOnePhoto": "1 photo and 1 story safely received",
  "share.complete.receivedPhotos": "{{count}} photos and 1 story safely received",
  "share.complete.copy": "Copy verification",
  "share.complete.download": "Download receipt",
  "share.complete.copied": "Verification copied",
  "share.complete.copyFailed": "Use download instead",
  "share.complete.downloaded": "Receipt downloaded",
  "share.complete.downloadTitle": "Gathered verification receipt for {{honoree}}",
  "share.complete.downloadPrivacy": "This verification record contains no contributor contact details or private invitation link.",
  "share.invite.eyebrow": "Private family collection",
  "share.invite.title": "This memory link needs its invitation.",
  "share.invite.body": "Please reopen the private link the family sent by text or email. It includes an unguessable invitation that lets you contribute without creating an account.",
  "share.invite.safety": "The invitation grants no access to anyone else’s photographs or stories.",
  "share.invite.back": "Back to Gathered",
  "share.headerPrivacy": "Private until the family approves it",
  "share.aside.remembering": "Remembering",
  "share.aside.defaultInvitation": "Help us gather the photographs, stories, sayings, and small moments that made {{honoree}} unforgettable.",
  "share.aside.quote": "The stories only you know are the ones we hope you’ll tell.",
  "share.aside.noAccount": "No account required",
  "share.aside.time": "About 5–10 minutes",
  "share.about.eyebrow": "A little about you",
  "share.about.title": "Who is sharing this memory?",
  "share.about.intro": "This helps the family understand the connection behind your story.",
  "share.about.name": "Your name",
  "share.about.namePlaceholder": "Your full name",
  "share.about.relationship": "How did you know {{honoree}}?",
  "share.about.relationshipPlaceholder": "Friend, cousin, neighbor, colleague…",
  "share.about.email": "Email",
  "share.about.emailPlaceholder": "For family follow-up",
  "share.about.phone": "Phone",
  "share.about.phonePlaceholder": "If phone is easier",
  "share.about.contactChoice": "email or phone required",
  "share.about.required": "required",
  "share.about.optional": "optional",
  "share.about.contactPrivacy": "Contact details are visible only to the family organizer—never published or added to a mailing list.",
  "share.photos.eyebrow": "Photographs with a story",
  "share.photos.title": "Add the moments you want remembered.",
  "share.photos.intro": "Photos are optional. Choose one or several from the same moment, then give each photograph the context only you know.",
  "share.photos.choose": "Choose photos",
  "share.photos.chooseMore": "Add more related photos",
  "share.photos.sources": "From your camera roll, photo library, or computer",
  "share.photos.limit": "Up to 12 related photos in this memory · 15 MB each · add unlimited memories",
  "share.photos.alt": "Your selected memory",
  "share.photos.number": "Photo {{number}}",
  "share.photos.remove": "Remove photo {{number}}",
  "share.photos.guidanceTitle": "Keep this memory centered on one moment.",
  "share.photos.guidanceBody": "Related photographs belong together here. If you have another time or story to share, add it as a new memory afterward—there’s no overall limit.",
  "share.photos.progress": "{{described}} of {{total}} photos described",
  "share.photos.progressHelp": "Every photograph keeps its own story.",
  "share.photos.trayLabel": "Selected photographs",
  "share.photos.described": "Story added",
  "share.photos.needsStory": "Needs a story",
  "share.photos.editorPosition": "Photo {{number}} of {{total}}",
  "share.photos.previous": "Previous photo",
  "share.photos.next": "Next photo",
  "share.photos.caption": "Tell us about this moment",
  "share.photos.captionPlaceholder": "When was it? Who was there? Why does this moment matter to you?",
  "share.photos.details": "Names, date, and place",
  "share.photos.peoplePlaceholder": "Who is pictured?",
  "share.photos.whenPlaceholder": "About when?",
  "share.photos.wherePlaceholder": "Where was this?",
  "share.photos.applyContext": "Fill blank details on every photo",
  "share.photos.applyContextHelp": "Copies these names, date, and place only where another photo is still blank.",
  "share.photos.contextApplied": "Details applied to {{count}} photos.",
  "share.photos.contextUndone": "Shared details were restored.",
  "share.photos.removed": "Photo removed from this memory.",
  "share.photos.restored": "Photo restored.",
  "share.photos.savedLocally": "{{count}} originals safely saved on this device",
  "share.photos.savedLocallyHelp": "They upload to the family’s protected collection only when you send this memory.",
  "share.photos.skip": "No photo? That’s completely fine. A story in your own words is just as meaningful.",
  "share.story.eyebrow": "The part only you know",
  "share.story.title": "Tell us one story about {{honoree}}.",
  "share.story.intro": "It doesn’t need to sound polished. Specific, ordinary details often become the memories people treasure most.",
  "share.story.withPhotosTitle": "What connects these photographs?",
  "share.story.withPhotosIntro": "You already gave all {{count}} photos their own context. A sentence or two about what ties these moments together is enough.",
  "share.story.connection": "What the family should know",
  "share.story.withPhotosPlaceholder": "Tell us what connects these photographs, or add the part of the memory that is not visible in them…",
  "share.story.withPhotosEncouragement": "A sentence or two is enough; you do not need to repeat the photo captions.",
  "share.story.starters": "Story starters",
  "share.story.prompt.smallThing": "A small thing she did that I’ll never forget…",
  "share.story.prompt.laugh": "The time she made everyone laugh…",
  "share.story.prompt.taught": "Something she taught me…",
  "share.story.prompt.place": "A place, tradition, or meal that reminds me of her…",
  "share.story.memory": "Your memory",
  "share.story.placeholder": "Write it the way you would tell it to someone who loved her…",
  "share.story.words": "{{count}} words",
  "share.story.encouragement": "A paragraph is wonderful. A page is welcome, too.",
  "share.story.when": "About when?",
  "share.story.whenPlaceholder": "Summer 1987, every Christmas…",
  "share.story.where": "Where?",
  "share.story.wherePlaceholder": "Her kitchen, Cannon Beach…",
  "share.story.aiTitle": "Only family editors can request a gentle clarity edit.",
  "share.story.aiBody": "Your original words always remain beside any suggestion. AI is instructed never to add facts, names, or details you didn’t provide—or to translate your voice without permission.",
  "share.review.eyebrow": "One last look",
  "share.review.title": "How may the family use your memory?",
  "share.review.intro": "You stay in control. The family reviews every contribution before it appears anywhere.",
  "share.review.serviceTitle": "Service and family materials",
  "share.review.serviceBody": "May be used in the slideshow, printed displays, program, and private family archive.",
  "share.review.publicTitle": "Public memorial too",
  "share.review.publicBody": "May also appear on the memorial website that friends and family can visit.",
  "share.review.privateTitle": "Family eyes only",
  "share.review.privateBody": "Keep this memory in the private family collection; do not publish or display it.",
  "share.review.recommended": "Recommended",
  "share.review.contribution": "Your contribution",
  "share.review.edit": "Edit story",
  "share.review.from": "From",
  "share.review.context": "Context",
  "share.review.legal": "By sending this memory, you confirm that you have the right to share these words and photographs and give the family permission to use them only within the scope selected above. You can ask the family to remove your contribution later.",
  "share.review.privacyLink": "Read the privacy details",
  "share.upload.hashing": "Checking each original…",
  "share.upload.creating": "Creating your private recovery record…",
  "share.upload.uploading": "Safely storing {{filename}}…",
  "share.upload.verifying": "Verifying both protected copies…",
  "share.upload.complete": "Everything is safely received.",
  "share.upload.photos": "{{completed}} of {{total}} photos verified",
  "share.upload.keepOpen": "Please keep this page open. Automatic retry is on.",
  "share.actions.back": "Back",
  "share.actions.leave": "Leave",
  "share.actions.continue": "Continue",
  "share.actions.continueNoPhotos": "Continue without photos",
  "share.actions.sending": "Sending with care…",
  "share.actions.send": "Send this memory",
  "share.actions.undo": "Undo",
  "share.autosave.ready": "Your words and selected originals are saved on this device as you go.",
  "share.autosave.blocked": "Keep this page open—this browser blocked local recovery storage.",
  "share.restore.title": "We restored your saved draft.",
  "share.restore.body": "Your words and selected originals are still here on this device.",
  "share.restore.dismiss": "Dismiss restored draft message",
  "voice.idleTitle": "Rather talk than type?",
  "voice.idleMessage": "Tell it naturally—we’ll turn your words into text.",
  "voice.recording": "Recording {{time}}",
  "voice.recordingMessage": "Listening… speak as if you’re telling this story to a friend.",
  "voice.transcribing": "Turning your voice into text…",
  "voice.added": "Added to your memory. You can edit every word.",
  "voice.failed": "We couldn’t transcribe that recording. Your recording was not saved—please try again.",
  "voice.unavailable": "Voice capture is not available in this browser. You can type your memory below.",
  "voice.empty": "Nothing was captured—please try again.",
  "voice.stopped": "Voice capture stopped. You can try again or type your memory.",
  "voice.startLabel": "Start voice recording",
  "voice.stopLabel": "Stop voice recording",
  "voice.workingLabel": "Transcribing voice recording",
  "turnstile.loading": "Preparing a quiet security check…",
  "privacy.back": "Back",
  "privacy.eyebrow": "Plain-language privacy",
  "privacy.title": "Your memory belongs to you and the family you shared it with.",
  "privacy.lead": "Gathered is designed for sensitive family material. The default is private, access is deliberate, and contributors choose how each memory may be used.",
  "privacy.privateTitle": "Private while collected",
  "privacy.privateBody": "New contributions are visible only in the family studio. Nothing appears in a memorial, slideshow, or print piece until a family editor approves it.",
  "privacy.permissionTitle": "Permission per memory",
  "privacy.permissionBody": "Contributors choose family-only, service materials, or public memorial use. The family cannot broaden that selection without asking.",
  "privacy.originalsTitle": "Originals preserved",
  "privacy.originalsBody": "Original words and photographs remain separate from edited copies. AI edits are suggestions and cannot overwrite or silently translate the source material.",
  "privacy.removalTitle": "Removal is possible",
  "privacy.removalBody": "Each contributor receives a private receipt. The family can locate the contribution, remove it from every published tribute, and process deletion from the private archive under the memorial’s retention plan.",
  "privacy.storesTitle": "What the system stores",
  "privacy.storesBody": "Your name, at least one contact method (email or phone), relationship, story, context fields, selected permission, submission language, and uploaded media. Contact details are used only if the family needs to follow up about the contribution. They are never published.",
  "privacy.aiTitle": "How AI is used",
  "privacy.aiBody": "Voice recordings are sent for transcription and discarded after text is returned. Family editors may request a clarity edit or theme summary. Prompts prohibit invention and preserve the original language; source material and proposed edits remain side by side.",
  "privacy.familyTitle": "Family responsibility",
  "privacy.familyBody": "The organizing family controls the collection and decides retention. Before launch, they set an archive or deletion date and a contact address for privacy requests."
} as const;

export type MessageKey = keyof typeof en;

const es: Record<MessageKey, string> = {
  "language.choose": "Elegir idioma",
  "language.english": "English",
  "language.spanish": "Español",
  "brand.home": "Inicio de Gathered",
  "brand.descriptor": "recuerdos unidos",
  "progress.label": "Paso {{current}} de {{total}}: {{label}}",
  "share.loadError": "La colección familiar no está disponible temporalmente. Su borrador permanecerá guardado en este dispositivo.",
  "share.steps.about": "Sobre usted",
  "share.steps.photos": "Fotos",
  "share.steps.story": "El recuerdo",
  "share.steps.review": "Revisar",
  "share.validation.name": "Por favor, escriba su nombre para que la familia sepa quién compartió este recuerdo.",
  "share.validation.contact": "Añada un correo electrónico o un número de teléfono para que la familia pueda comunicarse con usted sobre este recuerdo.",
  "share.validation.email": "Añada un correo electrónico válido para que la familia pueda comunicarse con usted sobre este recuerdo.",
  "share.validation.phone": "Revise el número de teléfono o déjelo en blanco.",
  "share.validation.photoStory": "Añada la historia de cada foto antes de continuar. Una o dos frases son suficientes.",
  "share.validation.storyDetail": "Cuéntenos un poco más; una o dos frases son suficientes.",
  "share.validation.storyRequired": "Añada el recuerdo que desea que la familia conserve.",
  "share.validation.security": "Complete la breve verificación de seguridad antes de enviar su recuerdo.",
  "share.validation.notImage": "{{filename}} no es una imagen. Elija una foto JPG, PNG, HEIC o WebP.",
  "share.validation.tooLarge": "{{filename}} supera los 15 MB. Elija una copia más pequeña.",
  "share.validation.tooMany": "Puede añadir hasta {{count}} fotos en un recuerdo. Después podrá compartir otro.",
  "share.submitError": "No pudimos enviar su recuerdo. El borrador sigue guardado en este dispositivo.",
  "share.complete.eyebrow": "Recuerdo recibido",
  "share.complete.thanks": "Gracias, {{name}}.",
  "share.complete.lead": "Su historia ya forma parte de la colección que la familia está reuniendo para {{honoree}}. La revisarán con cuidado antes de incluirla en cualquier homenaje.",
  "share.complete.meaningTitle": "Usted le dio significado a este recuerdo.",
  "share.complete.meaningBody": "Los nombres, el lugar y la historia que añadió convierten una fotografía en algo que la familia podrá conservar.",
  "share.complete.verifiedTitle": "Verificado y recuperable",
  "share.complete.verifiedBody": "Cada original fue verificado y guardado en dos ubicaciones protegidas antes de mostrar este recibo.",
  "share.complete.receipt": "Recibo",
  "share.complete.record": "Registro",
  "share.complete.moreTitle": "¿Tiene otro momento que compartir?",
  "share.complete.moreBody": "No hay límite de recuerdos. Su nombre y datos de contacto ya estarán completos.",
  "share.complete.another": "Añadir otro recuerdo",
  "share.complete.visit": "Ver el memorial",
  "share.complete.receivedNoPhotos": "1 historia recibida de forma segura",
  "share.complete.receivedOnePhoto": "1 foto y 1 historia recibidas de forma segura",
  "share.complete.receivedPhotos": "{{count}} fotos y 1 historia recibidas de forma segura",
  "share.complete.copy": "Copiar verificación",
  "share.complete.download": "Descargar recibo",
  "share.complete.copied": "Verificación copiada",
  "share.complete.copyFailed": "Use la descarga",
  "share.complete.downloaded": "Recibo descargado",
  "share.complete.downloadTitle": "Recibo de verificación de Gathered para {{honoree}}",
  "share.complete.downloadPrivacy": "Este registro no contiene datos de contacto ni el enlace privado de invitación.",
  "share.invite.eyebrow": "Colección familiar privada",
  "share.invite.title": "Este enlace necesita su invitación.",
  "share.invite.body": "Vuelva a abrir el enlace privado que la familia le envió por mensaje de texto o correo electrónico. Incluye una invitación segura que le permite contribuir sin crear una cuenta.",
  "share.invite.safety": "La invitación no permite ver las fotografías ni las historias de otras personas.",
  "share.invite.back": "Volver a Gathered",
  "share.headerPrivacy": "Privado hasta que la familia lo apruebe",
  "share.aside.remembering": "En memoria de",
  "share.aside.defaultInvitation": "Ayúdenos a reunir las fotografías, historias, frases y pequeños momentos que hicieron inolvidable a {{honoree}}.",
  "share.aside.quote": "Esperamos que comparta esas historias que solo usted conoce.",
  "share.aside.noAccount": "No necesita una cuenta",
  "share.aside.time": "Aproximadamente 5–10 minutos",
  "share.about.eyebrow": "Un poco sobre usted",
  "share.about.title": "¿Quién comparte este recuerdo?",
  "share.about.intro": "Esto ayuda a la familia a comprender la relación detrás de su historia.",
  "share.about.name": "Su nombre",
  "share.about.namePlaceholder": "Nombre completo",
  "share.about.relationship": "¿Cómo conoció a {{honoree}}?",
  "share.about.relationshipPlaceholder": "Amistad, familiar, vecino, colega…",
  "share.about.email": "Correo electrónico",
  "share.about.emailPlaceholder": "Para mensajes de la familia",
  "share.about.phone": "Teléfono",
  "share.about.phonePlaceholder": "Si prefiere el teléfono",
  "share.about.contactChoice": "correo o teléfono obligatorio",
  "share.about.required": "obligatorio",
  "share.about.optional": "opcional",
  "share.about.contactPrivacy": "Los datos de contacto solo son visibles para quien organiza el memorial; nunca se publican ni se añaden a una lista de correo.",
  "share.photos.eyebrow": "Fotografías con una historia",
  "share.photos.title": "Añada los momentos que desea recordar.",
  "share.photos.intro": "Las fotos son opcionales. Elija una o varias del mismo momento y dé a cada una el contexto que solo usted conoce.",
  "share.photos.choose": "Elegir fotos",
  "share.photos.chooseMore": "Añadir más fotos relacionadas",
  "share.photos.sources": "Desde la cámara, la fototeca o la computadora",
  "share.photos.limit": "Hasta 12 fotos relacionadas en este recuerdo · 15 MB cada una · recuerdos ilimitados",
  "share.photos.alt": "Su recuerdo seleccionado",
  "share.photos.number": "Foto {{number}}",
  "share.photos.remove": "Eliminar foto {{number}}",
  "share.photos.guidanceTitle": "Centre este recuerdo en un solo momento.",
  "share.photos.guidanceBody": "Las fotos relacionadas van juntas aquí. Si desea compartir otro momento o historia, añádalo después como un nuevo recuerdo; no hay límite total.",
  "share.photos.progress": "{{described}} de {{total}} fotos descritas",
  "share.photos.progressHelp": "Cada fotografía conserva su propia historia.",
  "share.photos.trayLabel": "Fotografías seleccionadas",
  "share.photos.described": "Historia añadida",
  "share.photos.needsStory": "Falta una historia",
  "share.photos.editorPosition": "Foto {{number}} de {{total}}",
  "share.photos.previous": "Foto anterior",
  "share.photos.next": "Foto siguiente",
  "share.photos.caption": "Cuéntenos sobre este momento",
  "share.photos.captionPlaceholder": "¿Cuándo fue? ¿Quién estaba allí? ¿Por qué es importante para usted?",
  "share.photos.details": "Nombres, fecha y lugar",
  "share.photos.peoplePlaceholder": "¿Quién aparece en la foto?",
  "share.photos.whenPlaceholder": "¿Aproximadamente cuándo?",
  "share.photos.wherePlaceholder": "¿Dónde fue?",
  "share.photos.applyContext": "Completar los detalles en blanco de todas las fotos",
  "share.photos.applyContextHelp": "Copia estos nombres, fecha y lugar solo en las fotos que aún no los tienen.",
  "share.photos.contextApplied": "Los detalles se aplicaron a {{count}} fotos.",
  "share.photos.contextUndone": "Se restauraron los detalles compartidos.",
  "share.photos.removed": "Se eliminó la foto de este recuerdo.",
  "share.photos.restored": "Se restauró la foto.",
  "share.photos.savedLocally": "{{count}} originales guardados de forma segura en este dispositivo",
  "share.photos.savedLocallyHelp": "Solo se cargarán a la colección protegida de la familia cuando envíe este recuerdo.",
  "share.photos.skip": "¿No tiene una foto? No hay problema. Una historia contada con sus propias palabras es igual de valiosa.",
  "share.story.eyebrow": "La parte que solo usted conoce",
  "share.story.title": "Cuéntenos una historia sobre {{honoree}}.",
  "share.story.intro": "No necesita sonar perfecta. Los detalles concretos y cotidianos suelen convertirse en los recuerdos más preciados.",
  "share.story.withPhotosTitle": "¿Qué conecta estas fotografías?",
  "share.story.withPhotosIntro": "Ya dio contexto a las {{count}} fotos. Basta una o dos frases sobre lo que une estos momentos.",
  "share.story.connection": "Lo que la familia debe saber",
  "share.story.withPhotosPlaceholder": "Cuéntenos qué conecta estas fotografías o añada la parte del recuerdo que no se ve en ellas…",
  "share.story.withPhotosEncouragement": "Una o dos frases son suficientes; no necesita repetir los pies de foto.",
  "share.story.starters": "Ideas para comenzar",
  "share.story.prompt.smallThing": "Un pequeño gesto suyo que nunca olvidaré…",
  "share.story.prompt.laugh": "La vez que hizo reír a todos…",
  "share.story.prompt.taught": "Algo que me enseñó…",
  "share.story.prompt.place": "Un lugar, tradición o comida que me recuerda a ella…",
  "share.story.memory": "Su recuerdo",
  "share.story.placeholder": "Escríbalo como se lo contaría a alguien que la quería…",
  "share.story.words": "{{count}} palabras",
  "share.story.encouragement": "Un párrafo es maravilloso. Una página también es bienvenida.",
  "share.story.when": "¿Aproximadamente cuándo?",
  "share.story.whenPlaceholder": "Verano de 1987, cada Navidad…",
  "share.story.where": "¿Dónde?",
  "share.story.wherePlaceholder": "Su cocina, la playa…",
  "share.story.aiTitle": "Solo la familia puede solicitar una edición suave para mejorar la claridad.",
  "share.story.aiBody": "Sus palabras originales siempre permanecerán junto a cualquier sugerencia. La IA tiene instrucciones de no añadir hechos, nombres ni detalles que usted no haya proporcionado, ni traducir su voz sin permiso.",
  "share.review.eyebrow": "Una última revisión",
  "share.review.title": "¿Cómo puede usar la familia su recuerdo?",
  "share.review.intro": "Usted mantiene el control. La familia revisa cada contribución antes de mostrarla.",
  "share.review.serviceTitle": "Ceremonia y materiales familiares",
  "share.review.serviceBody": "Puede usarse en la presentación, los paneles impresos, el programa y el archivo privado de la familia.",
  "share.review.publicTitle": "También en el memorial público",
  "share.review.publicBody": "También puede aparecer en el sitio web conmemorativo que visiten amistades y familiares.",
  "share.review.privateTitle": "Solo para la familia",
  "share.review.privateBody": "Conserve este recuerdo en la colección familiar privada; no lo publique ni lo exhiba.",
  "share.review.recommended": "Recomendado",
  "share.review.contribution": "Su contribución",
  "share.review.edit": "Editar el recuerdo",
  "share.review.from": "De",
  "share.review.context": "Contexto",
  "share.review.legal": "Al enviar este recuerdo, confirma que tiene derecho a compartir estas palabras y fotografías, y autoriza a la familia a usarlas únicamente dentro del alcance seleccionado. Puede pedir que retiren su contribución más adelante.",
  "share.review.privacyLink": "Leer los detalles de privacidad",
  "share.upload.hashing": "Verificando cada original…",
  "share.upload.creating": "Creando su registro privado de recuperación…",
  "share.upload.uploading": "Guardando de forma segura {{filename}}…",
  "share.upload.verifying": "Verificando ambas copias protegidas…",
  "share.upload.complete": "Todo se recibió de forma segura.",
  "share.upload.photos": "{{completed}} de {{total}} fotos verificadas",
  "share.upload.keepOpen": "Mantenga esta página abierta. Los reintentos son automáticos.",
  "share.actions.back": "Atrás",
  "share.actions.leave": "Salir",
  "share.actions.continue": "Continuar",
  "share.actions.continueNoPhotos": "Continuar sin fotos",
  "share.actions.sending": "Enviando con cuidado…",
  "share.actions.send": "Enviar este recuerdo",
  "share.actions.undo": "Deshacer",
  "share.autosave.ready": "Sus palabras y los originales seleccionados se guardan en este dispositivo mientras avanza.",
  "share.autosave.blocked": "Mantenga esta página abierta; el navegador bloqueó el almacenamiento local de recuperación.",
  "share.restore.title": "Restauramos su borrador guardado.",
  "share.restore.body": "Sus palabras y originales seleccionados siguen aquí en este dispositivo.",
  "share.restore.dismiss": "Cerrar el mensaje de borrador restaurado",
  "voice.idleTitle": "¿Prefiere hablar en vez de escribir?",
  "voice.idleMessage": "Cuéntelo con naturalidad y convertiremos sus palabras en texto.",
  "voice.recording": "Grabando {{time}}",
  "voice.recordingMessage": "Escuchando… hable como si le contara esta historia a una amistad.",
  "voice.transcribing": "Convirtiendo su voz en texto…",
  "voice.added": "Se añadió a su recuerdo. Puede editar cada palabra.",
  "voice.failed": "No pudimos transcribir la grabación. La grabación no se guardó; inténtelo de nuevo.",
  "voice.unavailable": "La grabación de voz no está disponible en este navegador. Puede escribir su recuerdo a continuación.",
  "voice.empty": "No se capturó nada; inténtelo de nuevo.",
  "voice.stopped": "La grabación se detuvo. Puede intentarlo de nuevo o escribir su recuerdo.",
  "voice.startLabel": "Iniciar grabación de voz",
  "voice.stopLabel": "Detener grabación de voz",
  "voice.workingLabel": "Transcribiendo la grabación",
  "turnstile.loading": "Preparando una breve verificación de seguridad…",
  "privacy.back": "Volver",
  "privacy.eyebrow": "Privacidad en lenguaje claro",
  "privacy.title": "Su recuerdo le pertenece a usted y a la familia con quien lo compartió.",
  "privacy.lead": "Gathered está diseñado para material familiar delicado. Todo es privado de forma predeterminada, el acceso es deliberado y cada persona decide cómo puede usarse su recuerdo.",
  "privacy.privateTitle": "Privado durante la recopilación",
  "privacy.privateBody": "Las nuevas contribuciones solo son visibles en el estudio familiar. Nada aparece en el memorial, la presentación o una pieza impresa hasta que la familia lo aprueba.",
  "privacy.permissionTitle": "Permiso para cada recuerdo",
  "privacy.permissionBody": "Cada persona elige entre uso familiar, materiales de la ceremonia o memorial público. La familia no puede ampliar esa selección sin pedir permiso.",
  "privacy.originalsTitle": "Originales preservados",
  "privacy.originalsBody": "Las palabras y fotografías originales se mantienen separadas de las copias editadas. Las ediciones con IA son sugerencias y no pueden reemplazar ni traducir silenciosamente el material original.",
  "privacy.removalTitle": "Puede solicitar que se retire",
  "privacy.removalBody": "Cada persona recibe un recibo privado. La familia puede localizar la contribución, retirarla de todos los homenajes publicados y gestionar su eliminación del archivo privado según el plan de conservación del memorial.",
  "privacy.storesTitle": "Qué almacena el sistema",
  "privacy.storesBody": "Su nombre, al menos un medio de contacto (correo electrónico o teléfono), relación, historia, datos de contexto, permiso seleccionado, idioma de envío y archivos cargados. Los datos de contacto solo se usan si la familia necesita comunicarse sobre la contribución. Nunca se publican.",
  "privacy.aiTitle": "Cómo se usa la IA",
  "privacy.aiBody": "Las grabaciones de voz se envían para su transcripción y se descartan después de devolver el texto. La familia puede solicitar una edición de claridad o un resumen de temas. Las instrucciones prohíben inventar y conservan el idioma original; la fuente y las sugerencias permanecen una junto a la otra.",
  "privacy.familyTitle": "Responsabilidad de la familia",
  "privacy.familyBody": "La familia organizadora controla la colección y decide cuánto tiempo conservarla. Antes del lanzamiento, establece una fecha de archivo o eliminación y una dirección de contacto para solicitudes de privacidad."
};

const messages: Record<SupportedLocale, Record<MessageKey, string>> = { en, es };

export function resolveLocale(queryLocale?: string | null, storedLocale?: string | null): SupportedLocale {
  if (queryLocale === "es" || queryLocale === "en") return queryLocale;
  if (storedLocale === "es" || storedLocale === "en") return storedLocale;
  return "en";
}

export function translate(locale: SupportedLocale, key: MessageKey, variables: Record<string, string | number> = {}): string {
  return messages[locale][key].replace(/\{\{(\w+)\}\}/g, (_match, name: string) => String(variables[name] ?? ""));
}

interface LocaleContextValue {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  t: (key: MessageKey, variables?: Record<string, string | number>) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<SupportedLocale>(() => {
    if (typeof window === "undefined") return "en";
    return resolveLocale(new URLSearchParams(window.location.search).get("lang"), window.localStorage.getItem(LOCALE_STORAGE_KEY));
  });

  const setLocale = useCallback((nextLocale: SupportedLocale) => {
    setLocaleState(nextLocale);
    if (typeof window === "undefined") return;
    window.localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale);
    const url = new URL(window.location.href);
    if (nextLocale === "es") url.searchParams.set("lang", "es");
    else url.searchParams.delete("lang");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback((key: MessageKey, variables?: Record<string, string | number>) => translate(locale, key, variables), [locale]);
  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error("useLocale must be used within LocaleProvider");
  return value;
}
